import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../../design/AppContext";
import { HIDE_DEMO_KEY } from "../../utils/request";
import { apiGet, apiPutForm } from "../../utils/api";
import { jwtEncode } from "../../utils/jwt";
import { apiErrorMessage } from "../../utils/apiError";
import { hasPermission, actionPermissions } from "../../utils/permission";
import { getTextSize, setTextSize, TEXT_SIZES } from "../../utils/textsize";
import { getTheme, setTheme } from "../../utils/theme";
/* 提醒門檻的預設值只有一份，就放在算黃燈的那個檔案裡（見 BUFFER_CHOICES 的說明） */
import { DEFAULT_BUFFER_DAYS } from "../Schedule/rdSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { MdWarningAmber } from "react-icons/md";
import { Banner, Field, PasswordField, RowButton, StatusChip } from "../../components/mui";
import PasswordChangeForm from "../../components/auth/PasswordChangeForm";
import { InlineLoading, SkeletonRows } from "../../components/loading";

/**
 * 設定（設計稿 #screen-setting，el-pm-template_4.html:2007-2091）
 *
 * 舊版這一頁是四塊灰底卡片加一排自製的 40×22 撥桿，撥桿沒有文字、命中區只有
 * 22px 高，而且「我要收哪些通知」用兩欄打勾的表格呈現 —— 使用者得先看懂表頭
 * 才知道自己在勾什麼。新版整頁改成一句話一個 44px 的 checkbox，每一項自己
 * 把「什麼時候會通知你」寫完。
 *
 * 版面規則：
 *   · 不自己包頁面外框，Layout 的內容區已經處理好寬度與留白。
 *   · 整頁分成幾個分頁（見 TABS）。原本八張卡一路往下排，要找「提醒門檻」得捲過
 *     整頁的通知勾選 —— 使用者的原話是「設定內還是太長」。
 *   · 卡片不再寫 max-width:720px：那一行加上 Layout 的 1120px 上限，卡片只佔畫面
 *     左邊三分之一，右邊整片空白（README 也說寬度上限看 NO_PAGE_CAP，不要在頁面裡自己寫）。
 *     行長改由欄位自己控制：短欄位用 FORMGRID 並排，下拉與前綴這種只放幾個字的
 *     輸入框才限制輸入框本身的寬度（NARROW），卡片一律撐滿。
 *   · 同一個分頁裡最多一顆 contained 主要按鈕（硬規則 1）。只有「我的帳號」那顆
 *     「存起來」是 contained；其他分頁要嘛沒有存檔鈕（勾了就存），要嘛有兩顆以上
 *     各管各的存檔鈕 —— 哪一顆都不比另一顆重要，全部退成 outlined。
 *
 * 「不刪任何功能」的實際出口在「其他功能」分頁：改版後側欄只剩六個入口，
 * 儀表板、報表、工時、稽核… 這些頁面都還在，只是沒有入口。列在這裡
 * 之後它們仍然一鍵可達（硬規則 8）。
 */

/* ===================== 常數 ===================== */

/** 卡與卡之間的間距。刻意不再帶 max-width（原因見檔頭） */
const CARD = { mt: 3 };

/** 短欄位並排；窄螢幕與大字模式（欄寬放不下 320px）自動回單欄 */
const FORMGRID = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 3 };

/**
 * 只放幾個字的輸入框（天數下拉、代碼前綴）限制輸入框本身的寬度，不是限制整張卡：
 * 卡片撐滿之後，一條 1000px 的下拉裡只有「3 天」兩個字，眼睛得橫跨整個畫面才看得到箭頭。
 */
const NARROW = { maxWidth: 280 };

/**
 * 分頁（網址上的 ?tab=）。
 *
 * 分法看「誰會來改、多久改一次」，不是看資料存在哪裡：
 *   · account  我的帳號：姓名、Email、改密碼 —— 每個人都有、偶爾改。
 *   · notify   通知：一長串勾選，是整頁最長的一塊，單獨一頁才不會把別的擠到下面去。
 *   · display  顯示：字級、深色，加上「示範資料」。示範資料原本排在管理區附近，
 *              但它是存在每個人自己瀏覽器裡的個人偏好（localStorage），任何人都要能關，
 *              放進「系統管理」的話，沒有管理權限的人就再也藏不掉示範專案。
 *   · remind   提醒：里程碑與工項的兩個門檻。同一支 /Setting/Goal、同一封每日提醒信，
 *              放在一起才看得出它們是一組；沒權限的人在這裡看得到現值（唯讀）。
 *   · other    其他功能：沒進側邊欄的畫面。它是「不刪功能」的出口，每個人都要看得到，
 *              所以不併進「系統管理」—— 那一頁一般使用者根本看不到。
 *   · admin    系統管理：帳號與角色、編碼規則、SMTP，以及只有管理者會開的那幾個畫面入口。
 *              沒有任何管理權限的人整個分頁不畫（硬規則 2）。
 * 管理放最後：它是最少人會開、而且只有部分人看得到的一頁，放中間的話其他分頁的位置會因人而異。
 */
const TABS = [
  { key: "account", label: "我的帳號" },
  { key: "notify", label: "通知" },
  { key: "display", label: "顯示" },
  { key: "remind", label: "提醒" },
  { key: "other", label: "其他功能" },
  { key: "admin", label: "系統管理" },
];

/** 設計稿 L2036 的 44px 可點列（硬規則 3）。說明文字換行時要靠上對齊 */
const CHECK_ROW = { minHeight: 44, alignItems: "flex-start", m: 0, py: 1 };

/**
 * 側欄入口以外、仍然活著的舊畫面。
 *
 * 改版把側欄從 22 列收成 6 個入口，這些頁面因此沒有了入口 —— 但頁面、路由、
 * 後端端點都還在（resources/menu.js 裡它們是 hidden 而不是被刪掉）。
 * 沒有這一段，「不刪任何功能」就只是嘴上說說：使用者再也找不到它們。
 *
 * permission 與 menu.js 同一份模組碼；沒有權限的人看到的是一條 403 死路，
 * 所以先濾掉再畫（硬規則 2：空的、走不通的東西不要畫出來）。
 *
 * admin:true 的那幾個只有管理者會開，看得到「系統管理」分頁時就排進那一頁，
 * 跟帳號與角色管理放在一起；看不到那一頁的人（例如只有 setting 檢視權限）
 * 則留在「其他功能」—— 不能因為分頁不畫，就讓他有權限的畫面跟著沒有入口。
 */
const OTHER_PAGES = [
  { label: "儀表板", path: "/Dashboard", desc: "全公司的統計數字與活動紀錄", permission: "dashboard" },
  { label: "報表與分析", path: "/Reports", desc: "專案與工項的統計圖表", permission: "report" },
  { label: "每日工項紀錄", path: "/Timesheet", desc: "本週每日的工項紀錄與未填寫日期", permission: "task" },
  { label: "稽核紀錄", path: "/Audit", desc: "系統異動紀錄（人員、時間、內容）", permission: "audit" },
  { label: "自動化規則", path: "/Automation", desc: "符合條件時自動變更狀態或指派", permission: "setting", admin: true },
  { label: "團隊績效", path: "/Performance/Team", desc: "團隊成員的完成量與準時率", permission: "performance" },
  { label: "個人績效", path: "/Performance/Report", desc: "個人的完成量與準時率", permission: "performance" },
  { label: "國定假日", path: "/Setting/Holiday", desc: "排定工期時略過的日期", permission: "setting", admin: true },
  { label: "時程日曆", path: "/Schedule/Calendar", desc: "月曆格式的專案與工項排程", permission: "schedule" },
  { label: "角色權限矩陣", path: "/Setting/Roles/Matrix", desc: "逐格勾選每個角色的權限", permission: "role", admin: true },
  { label: "畫面索引", path: "/Map", desc: "系統所有畫面的總覽", permission: null },
];

/**
 * 提醒門檻的選項（設計稿 L2058-2065）。
 *
 * 預設值直接引用 rdSummary.js 的 DEFAULT_BUFFER_DAYS，不要在這裡再寫一次 3 ——
 * 那個常數是時程頁「讀不到設定時」的退路，兩邊寫成不同的數字的話，
 * 這一頁會把別的天數標成「（預設）」。
 * 後端收 1..30，所以真正存進去的值若不在這三個選項裡，畫面上會另外補一列（見 bufferOptions）。
 */
const BUFFER_CHOICES = [DEFAULT_BUFFER_DAYS, 5, 7];

/**
 * 工項到期提醒的門檻（後端 system_setting 的 task.due_soon_days，同一支 /Setting/Goal）。
 *
 * 這個預設值刻意不放進 rdSummary.js 跟 DEFAULT_BUFFER_DAYS 作伴：那一個是「時程頁
 * 讀不到設定也還是要畫得出黃燈」的退路，前端真的拿它去算東西；工項門檻只有後端
 * 每天那封提醒信在用，前端沒有任何一頁會拿它做判斷 —— 這裡需要它，只是為了在下拉裡
 * 標出哪一個是「（預設）」。真正生效的預設值在後端，兩邊萬一對不上，畫面上也只是
 * 少標一個「（預設）」，不會有任何數字被算錯。
 */
const DEFAULT_TASK_DUE_DAYS = 3;

/** 工項門檻的選項。後端同樣收 1..30，非標準值的補列規則見 taskDueOptions */
const TASK_DUE_CHOICES = [DEFAULT_TASK_DUE_DAYS, 5, 7];

/**
 * 通知項目的正式用語對照（以後端回傳的 r.label 為 key）。
 * 後端文字改好後可移除。只換顯示文字，togglePref 送出的仍是 r.type；對不到的照舊顯示後端原文。
 */
const PREF_TEXT = {
  工項指派: { label: "工項指派", desc: "工項指派給您時通知。" },
  留言提及我: { label: "留言提及", desc: "有人在留言中以 @ 提及您時通知。" },
  新留言: { label: "新留言", desc: "您追蹤或負責的工項有新留言時通知。" },
  狀態變更: { label: "狀態變更", desc: "您追蹤或負責的工項狀態變更時通知。" },
  審核與簽核: { label: "審核與簽核", desc: "有工項送交您審核，或您送出的審核有結果時通知。" },
  即將到期: { label: "即將到期", desc: "您負責的工項即將到期時通知。" },
  已逾期: { label: "已逾期", desc: "您負責的工項已逾期時通知。" },
  風險與議題: { label: "風險與議題", desc: "您所屬專案的風險或議題有異動時通知。" },
  其他異動: { label: "其他異動", desc: "專案、成員、排序等其他異動時通知。" },
};

const SECURE_OPTIONS = [
  { v: "starttls", label: "STARTTLS（587）" },
  { v: "ssl", label: "SSL/TLS（465）" },
  { v: "none", label: "不加密（25，內部轉遞）" },
];

/* ===================== 共用零件 ===================== */

/**
 * 一句話一個勾（設計稿 L2036-L2039）。
 *
 * 取代舊版自製的 Toggle 元件：那顆撥桿沒有文字、命中區 22px，
 * 開跟關只差一個顏色，而顏色正是色弱使用者最不該被要求分辨的東西。
 */
function CheckRow({ checked, onChange, text, meta }) {
  return (
    <FormControlLabel
      sx={CHECK_ROW}
      control={<Checkbox checked={checked} onChange={onChange} sx={{ mt: -1 }} />}
      label={
        <Box component="span" sx={{ display: "block" }}>
          <Typography component="span" sx={{ display: "block" }}>
            {text}
          </Typography>
          {meta ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ display: "block" }}>
              {meta}
            </Typography>
          ) : null}
        </Box>
      }
    />
  );
}

/** 卡片：外框＋標題。標題是 h2，視覺用 section 那一級 */
function SectionCard({ title, children, sx }) {
  return (
    <Card component="section" sx={sx}>
      <CardContent>
        {title ? (
          <Typography variant="h3" component="h2">
            {title}
          </Typography>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

SectionCard.propTypes = { title: PropTypes.node, children: PropTypes.node, sx: PropTypes.object };
SectionCard.defaultProps = { title: null, children: null, sx: {} };

/** 單選切換（字級、深淺色）。高度跟其他按鈕一樣走 --t-control-h */
const TOGGLE_SX = { "& .MuiToggleButton-root": { minHeight: "var(--t-control-h)", px: 3, textTransform: "none" } };

CheckRow.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  text: PropTypes.string.isRequired,
  meta: PropTypes.string,
};
CheckRow.defaultProps = { checked: false, onChange: () => {}, meta: "" };

/* ===================== 畫面 ===================== */

function Preferences(props) {
  const navigate = useNavigate();
  const { toast, userInfo } = useApp();
  const {
    smtp,
    prefs,
    canEditSmtp,
    canManageAdmin,
    GET_Smtp,
    PUT_Smtp,
    POST_SmtpTest,
    GET_NotificationPref,
    PUT_NotificationPref,
  } = props;

  /* ---------- 我的資料 ---------- */

  const myName = _.get(userInfo, "name", _.get(userInfo, "Name", ""));
  const myEmail = _.get(userInfo, "email", _.get(userInfo, "Email", ""));
  const myAccount = _.get(userInfo, "account", _.get(userInfo, "Account", ""));
  const [profileName, setProfileName] = useState(myName);
  const [profileEmail, setProfileEmail] = useState(myEmail);
  const [savingProfile, setSavingProfile] = useState(false);

  // userInfo 是登入後才灌進 context 的，第一次拿到時把欄位補上（使用者已經在打字就不要蓋掉）
  const profileTouched = useRef(false);
  useEffect(() => {
    if (profileTouched.current) return;
    if (myName || myEmail) {
      setProfileName(myName);
      setProfileEmail(myEmail);
      profileTouched.current = true;
    }
  }, [myName, myEmail]);

  /* ---------- 顯示方式 ---------- */

  /*
   * 字級與深淺色的真正狀態掛在 <html> 上（utils/textsize.js、utils/theme.js），
   * 頁首那組按鈕與這裡是同一份資料的兩個入口。
   *
   * 用 MutationObserver 盯著 <html> 的屬性而不是自己記一份 state：
   * 使用者可能在頁首按、也可能在這裡按，兩邊都要立刻反映。
   * （反過來，頁首那組是 Layout.js 的 React state，這一頁改完它的 aria-pressed
   *   會暫時停在舊值 —— Layout.js 不在這次的檔案清單裡，等它改成同樣讀 DOM
   *   就會一致。功能本身兩邊都正確，只差按鈕的按下樣式。）
   */
  const [textSize, setTextSizeState] = useState(() => getTextSize());
  const [theme, setThemeState] = useState(() => getTheme());
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => {
      setTextSizeState(getTextSize());
      setThemeState(el.getAttribute("data-theme") === "dark" ? "dark" : "light");
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(el, { attributes: true, attributeFilter: ["data-textsize", "data-theme"] });
    return () => mo.disconnect();
  }, []);

  const applySize = (v) => setTextSizeState(setTextSize(v));
  const applyTheme = (dark) => {
    const next = dark ? "dark" : "light";
    setTheme(next);
    setThemeState(next);
  };

  /* ---------- 里程碑提醒門檻 ---------- */

  /*
   * 設計稿 L2058-2065 的「提前幾天提醒」。
   *
   * 這個數字存在後端 system_setting 的 goal.buffer_days（GET/PUT /Setting/Goal），
   * 時程頁的里程碑就是拿它決定什麼時候轉黃燈（pages/Schedule/useGoalBufferDays.js）。
   * 走 apiGet/apiPutForm 不進 redux，理由同下面的專案編碼規則。
   *
   * null ＝「還沒讀回來」，不是 0 也不是預設值：先畫一個 3 再跳成 7 的話，
   * 看起來像自己的設定被改掉了。
   */
  const [buffer, setBuffer] = useState(null);
  /* 後端原本存的那個值要另外記著，不能只靠 buffer ——
     buffer 會跟著使用者選，記在它上面的話一改選就把原值弄丟了（見 bufferOptions） */
  const [bufferSaved, setBufferSaved] = useState(null);
  const [bufferFailed, setBufferFailed] = useState(false);
  const [savingBuffer, setSavingBuffer] = useState(false);

  /* ---------- 工項到期提醒門檻 ---------- */

  /*
   * 工項的門檻跟里程碑的門檻是同一支端點的兩個值（GET/PUT /Setting/Goal），
   * 所以只發一次請求、兩個值各自收進自己的 state。
   * 三種狀態（還沒讀回來 / 讀不到 / 讀到了）與命名一律照上面那一組，
   * 兩區的行為要一模一樣，不另外發明一套。
   */
  const [taskDue, setTaskDue] = useState(null);
  const [taskDueSaved, setTaskDueSaved] = useState(null);
  const [taskDueFailed, setTaskDueFailed] = useState(false);
  const [savingTaskDue, setSavingTaskDue] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGet("/Setting/Goal")
      .then((res) => {
        if (!alive) return;
        const n = Number(_.get(res, "buffer_days"));
        /* 形狀不對就當作讀不到：塞進下拉會變成一個空白選項，看起來像「沒設定過」，
           但實際上是我們沒讀懂回來的東西，而且按下存檔還會把原本的值蓋掉 */
        if (!Number.isFinite(n) || n <= 0) setBufferFailed(true);
        else {
          setBuffer(String(n));
          setBufferSaved(n);
        }

        /*
         * 兩個值要各自判斷，不能互相牽連 —— 原本這裡讀不到就 return，
         * 現在改成 else：task_due_soon_days 是這支端點後來才多出來的欄位，
         * 後端還沒佈上去時回的 JSON 裡沒有它，那就只有工項那一區說「讀不到」，
         * 里程碑的門檻明明讀得到，不該跟著消失；反過來也一樣。
         */
        const m = Number(_.get(res, "task_due_soon_days"));
        if (!Number.isFinite(m) || m <= 0) setTaskDueFailed(true);
        else {
          setTaskDue(String(m));
          setTaskDueSaved(m);
        }
      })
      /* 讀這兩個門檻只要登入就有權限（GET /Setting/Goal 沒掛 setting 權限），
         所以失敗就是連線或伺服器的問題 —— 那要講出來，不能留一個空欄位 */
      .catch(() => {
        if (!alive) return;
        setBufferFailed(true);
        setTaskDueFailed(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  /*
   * 後端收 1..30。別人（或 SQL）設成 4 天時要看得見自己的值，
   * 不然下拉停在空選項，一按存檔就被改成 3 天。
   *
   * 補的那一列要從「載入回來的值」推，不是從「目前選取值」推。
   * 從選取值推的話，使用者一改選其他選項，原本那個非標準值就從已經展開的下拉裡消失了 ——
   * 誤點之後沒有任何辦法改回去（只能整頁重新整理），沒注意直接存檔就把全公司的門檻改掉了。
   */
  const bufferOptions = useMemo(() => {
    const n = Number(bufferSaved);
    if (!Number.isFinite(n) || n <= 0 || BUFFER_CHOICES.includes(n)) return BUFFER_CHOICES;
    return [...BUFFER_CHOICES, n].sort((a, b) => a - b);
  }, [bufferSaved]);

  /* 工項門檻的補列規則與上面完全相同（理由見 bufferOptions），只是換一組選項 */
  const taskDueOptions = useMemo(() => {
    const n = Number(taskDueSaved);
    if (!Number.isFinite(n) || n <= 0 || TASK_DUE_CHOICES.includes(n)) return TASK_DUE_CHOICES;
    return [...TASK_DUE_CHOICES, n].sort((a, b) => a - b);
  }, [taskDueSaved]);

  /* ---------- 示範資料 ---------- */

  /* 預設隱藏：沒設定過就是隱藏，只有明確關掉才會存成 "0"（見 utils/request.js） */
  const [hideDemo, setHideDemo] = useState(localStorage.getItem(HIDE_DEMO_KEY) !== "0");

  const toggleDemo = () => {
    const next = !hideDemo;
    setHideDemo(next);
    // 關掉要明確存 "0" 而不是刪掉 key：預設是隱藏，刪掉等於下次又被隱藏回去
    localStorage.setItem(HIDE_DEMO_KEY, next ? "1" : "0");
    toast(next ? "已隱藏示範資料" : "已顯示示範資料");
    // 各頁的資料在掛載時就抓好了，整頁重載最單純也最不會有殘留
    setTimeout(() => window.location.reload(), 600);
  };

  /* ---------- 專案編碼規則 ---------- */

  /*
   * 走 apiGet/apiPutForm 而不進 redux —— 這是一個只有本頁會用到的單一設定值，
   * 為它加一組 service/saga/reducer 只是多三個檔案要維護。
   */
  const [codeRule, setCodeRule] = useState({ prefix: "", format: "", sample: "" });
  const [prefixInput, setPrefixInput] = useState("");
  const [savingCode, setSavingCode] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGet("/Project/CodeRule")
      .then((res) => {
        if (!alive || !res) return;
        setCodeRule(res);
        setPrefixInput(_.get(res, "prefix", ""));
      })
      // 沒有專案模組權限就看不到編碼規則，靜靜留白即可
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  /* ---------- SMTP / 通知訂閱 ---------- */

  /** SMTP 表單。密碼永遠不會從後端回來，欄位留空就代表「不更動」 */
  const [form, setForm] = useState(null);
  const [password, setPassword] = useState("");
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadSmtp = useCallback(() => {
    if (!canEditSmtp) return;
    GET_Smtp(null, null, null);
  }, [canEditSmtp, GET_Smtp]);

  useEffect(() => {
    loadSmtp();
    GET_NotificationPref(null, null, null);
  }, [loadSmtp, GET_NotificationPref]);

  // 後端回來後才鋪表單；使用者已經在改的話就不要覆蓋掉他打到一半的內容
  useEffect(() => {
    if (smtp && !form) setForm({ ...smtp });
  }, [smtp, form]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const prefList = Array.isArray(prefs) ? prefs : [];

  /*
   * 系統現在有沒有在寄信："loading" | "on" | "off" | "unknown"（讀不到）。
   *
   * 原本只看 smtp，可是 smtp 只有 setting.edit 的人才會載入（見 loadSmtp）——
   * 一般使用者的 mailOn 永遠是 false，「也寄 Email 給我」整排不畫，頁面還說系統不寄信；
   * 實際上 SMTP 開著、沒設定過的類別預設就要寄，他收到信卻沒有地方關。
   * /Setting/Smtp/Status 只要登入就讀得到，判斷與後端實際排信是同一份（MailService.DeliveryOn）。
   *
   * 讀不到（離線、端點還沒佈上去）要記成 unknown，不能當成 off：
   * 當成 off 就是又回到「明明在寄、卻說沒在寄、還沒得關」的老問題。
   */
  const [mailStatus, setMailStatus] = useState("loading");
  const mailAlive = useRef(true);
  const loadMailStatus = useCallback(() => {
    apiGet("/Setting/Smtp/Status")
      .then((res) => {
        if (!mailAlive.current) return;
        const v = _.get(res, "enabled");
        // 形狀不對就當作讀不到，理由同 /Setting/Goal 那一段
        setMailStatus(v === true ? "on" : v === false ? "off" : "unknown");
      })
      .catch(() => {
        if (mailAlive.current) setMailStatus("unknown");
      });
  }, []);
  useEffect(() => {
    mailAlive.current = true;
    loadMailStatus();
    return () => {
      mailAlive.current = false;
    };
  }, [loadMailStatus]);

  /*
   * 通知分頁要照哪個狀態畫。有 setting.edit 的人照舊看 SMTP 表單 —— 他可能剛勾了啟用、
   * 還沒按存起來。沒啟用或沒填主機都算沒在寄，跟後端 DeliveryOn 同一個判斷。
   * 表單還沒鋪好（載入中或讀失敗）時退回 Status。
   */
  const mailState =
    canEditSmtp && form ? (form.enabled && String(form.host || "").trim() ? "on" : "off") : mailStatus;
  /* 讀不到時勾選照樣畫出來：偏好本身存得進去，系統有在寄的話就照它寄。藏起來等於不讓人關 */
  const showEmailRows = mailState === "on" || mailState === "unknown";
  /* 表單（還沒存）跟已存的值講的不一樣：只有兩邊都確定時才算，讀不到的那一邊不拿來比 */
  const mailPending =
    (mailState === "on" || mailState === "off") && (mailStatus === "on" || mailStatus === "off") && mailState !== mailStatus;

  /* ---------- 送出 ---------- */

  const saveProfile = async () => {
    if (savingProfile) return;
    if (!String(profileName || "").trim()) {
      toast("請填寫姓名", "error");
      return;
    }
    setSavingProfile(true);
    try {
      // 後端是 [FromForm] ReqPost{Data}，內容要用 jwtEncode 包起來（同其他 Auth 端點）
      const name = profileName.trim();
      const email = String(profileEmail || "").trim();
      await apiPutForm("/Auth/Profile", { data: jwtEncode({ Name: name, Email: email }) });

      /*
       * 存完要自己把 Redux 裡的 userInfo 更新掉。
       *
       * userInfo 不是打 API 拿的，是從 access_token 解出來的
       * （sagas/Global.js 的 INITIAL：decodeJwt(token) → SAVE_UserInfo）。
       * 改資料庫不會換 token，所以不補這一步的話，資料其實存進去了，
       * 畫面上卻還是舊的 —— 而且上一版的提示寫「重新整理後就會顯示新的姓名」
       * 是錯的：重新整理只是把同一個 token 再解一次，一樣是舊值。
       *
       * 只蓋 name / email 兩個鍵，其餘（account、role、permission）原封不動 ——
       * 那些是授權用的，不該由這個表單碰。下次登入拿到的新 token 自然就對了。
       */
      props.SAVE_UserInfo({ ...userInfo, name, Name: name, email, Email: email });
      toast("已更新個人資料");
    } catch (e) {
      toast(apiErrorMessage(e, "更新個人資料失敗"), "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveCodeRule = async () => {
    if (savingCode) return;
    setSavingCode(true);
    try {
      // 後端會再做一次正規化（只留英數、轉大寫、上限 8 碼），回來的才是實際存進去的值
      const next = await apiPutForm("/Project/CodeRule", { prefix: prefixInput });
      if (next) {
        setCodeRule(next);
        setPrefixInput(_.get(next, "prefix", ""));
      }
      toast("專案代碼前綴已儲存");
    } catch (e) {
      toast(apiErrorMessage(e, "儲存失敗"), "error");
    } finally {
      setSavingCode(false);
    }
  };

  const saveBuffer = async () => {
    if (savingBuffer || buffer === null) return;
    setSavingBuffer(true);
    try {
      /* 後端是 [FromForm] int bufferDays（同編碼規則那一支的形狀，不是 data=<JWT> 那條路）。
         回來的才是實際存進去的值，超出 1..30 後端會退回錯誤，不會默默夾成別的數字 */
      const next = await apiPutForm("/Setting/Goal", { bufferDays: Number(buffer) });
      const n = Number(_.get(next, "buffer_days"));
      if (Number.isFinite(n) && n > 0) {
        setBuffer(String(n));
        // 存檔成功之後這個才是「後端存的值」，下拉要補哪一列跟著它走
        setBufferSaved(n);
      }
      toast("提醒門檻已儲存，時程頁的里程碑將依此天數轉為黃燈");
    } catch (e) {
      toast(apiErrorMessage(e, "儲存失敗"), "error");
    } finally {
      setSavingBuffer(false);
    }
  };

  const saveTaskDue = async () => {
    if (savingTaskDue || taskDue === null) return;
    setSavingTaskDue(true);
    try {
      /*
       * PUT /Setting/Goal 的兩個參數都是選擇性的，只送 taskDueSoonDays 就只改工項這一個。
       * 刻意不把 bufferDays 一起送回去：上面那個值可能是剛才讀不到、或是別人同時改過的，
       * 連帶送出等於拿畫面上的舊值蓋掉後端的新值。
       */
      const next = await apiPutForm("/Setting/Goal", { taskDueSoonDays: Number(taskDue) });
      const n = Number(_.get(next, "task_due_soon_days"));
      if (Number.isFinite(n) && n > 0) {
        setTaskDue(String(n));
        setTaskDueSaved(n);
      }
      toast("工項到期提醒門檻已儲存，每日提醒信將依此天數篩選工項");
    } catch (e) {
      toast(apiErrorMessage(e, "儲存失敗"), "error");
    } finally {
      setSavingTaskDue(false);
    }
  };

  const saveSmtp = () => {
    if (!form) return;
    PUT_Smtp(
      {
        enabled: !!form.enabled,
        host: form.host || "",
        port: Number(form.port) || 587,
        secure: form.secure || "starttls",
        user: form.user || "",
        password: password || "",
        from_address: form.from_address || "",
        from_name: form.from_name || "",
        base_url: form.base_url || "",
      },
      (next) => {
        setPassword("");
        if (next) setForm({ ...next });
        // 系統管理分頁那條「目前系統沒有寄送郵件」看的是 Status（已存的值），存完要重讀才會跟著變
        loadMailStatus();
        toast("郵件設定已儲存");
      },
      setSaving
    );
  };

  const sendTest = () => {
    POST_SmtpTest({ to: testTo || "" }, (msg) => toast(msg || "測試信已寄出"), setTesting);
  };

  /* 只翻一個開關，另一個照原樣送回去 —— 不帶的話後端會把它當成「取消訂閱」 */
  const togglePref = (row, channel) => {
    PUT_NotificationPref({
      type: row.type,
      in_app: channel === "in_app" ? !row.in_app : row.in_app,
      email: channel === "email" ? !row.email : row.email,
    });
  };

  /* ---------- 其他功能 ---------- */

  /*
   * 權限碼對不上就不畫那一列。
   *
   * 例外：token 的 permission claim 讀不到（解析失敗、或舊 token 沒有這個 claim）時
   * 一律全部顯示 —— 這一區是「不刪功能」的唯一出口，寧可讓使用者按下去撞到
   * NoPermission，也不要因為讀不到權限就整區消失、讓人以為功能被刪了。
   */
  const otherPages = useMemo(() => {
    const codes = actionPermissions();
    if (codes.length === 0) return OTHER_PAGES;
    return OTHER_PAGES.filter((p) => !p.permission || hasPermission(p.permission));
  }, []);

  /* ---------- 分頁 ---------- */

  /* 「系統管理」只給有管理權限的人（硬規則 2）。編碼規則的唯讀值不算：那只是一行字，
     為它給一般使用者開一整個叫「系統管理」的分頁，只會讓人以為自己能管什麼；
     唯讀值改放「其他功能」（見 codeRuleCard） */
  const showAdminTab = canEditSmtp || canManageAdmin;
  const adminPages = showAdminTab ? otherPages.filter((p) => p.admin) : [];
  const plainPages = showAdminTab ? otherPages.filter((p) => !p.admin) : otherPages;

  /* 沒有管理分頁的人，編碼規則的唯讀值放在「其他功能」，所以它也算這一頁有東西 */
  const otherHasContent = plainPages.length > 0 || (!showAdminTab && !!codeRule.prefix);
  const tabs = useMemo(
    () => TABS.filter((t) => (t.key === "admin" ? showAdminTab : t.key === "other" ? otherHasContent : true)),
    [showAdminTab, otherHasContent]
  );

  /*
   * 選到哪個分頁記在網址上（?tab=notify），重新整理與上一頁才回得到同一個分頁；
   * 作法照 Schedule/Goals.js 的 setProjSel。直接從網址推、不另外記一份 state，
   * 兩份就不會對不上。網址上的值不存在（打錯、或是沒權限的 admin）就回第一頁。
   */
  const [params, setParams] = useSearchParams();
  const wantedTab = params.get("tab");
  const activeTab = tabs.some((t) => t.key === wantedTab) ? wantedTab : tabs[0].key;
  const selectTab = useCallback(
    (key) => {
      /* replace 不是 push：切分頁是「看同一頁的另一塊」，不該在上一頁堆出一長串歷史 */
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", key);
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  /* ===================== 版面 ===================== */

  /* 一列一個畫面入口；「其他功能」與「系統管理」兩個分頁共用同一種列 */
  const pageRows = (list) => (
    <Card sx={{ mt: 2 }}>
      <List disablePadding>
        {list.map((p) => (
          <RowButton key={p.path} title={p.label} meta={<span>{p.desc}</span>} onClick={() => navigate(p.path)} />
        ))}
      </List>
    </Card>
  );

  /* ---------- 我的帳號：左邊是個人資料、右邊是變更密碼（各自一張卡、各自一顆送出） ---------- */
  const accountPanel = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 3,
        alignItems: "start",
        '[data-textsize="lg"] &': { gridTemplateColumns: "1fr" },
      }}
    >
      <SectionCard title="我的資料" sx={CARD}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Field
            label="姓名"
            htmlFor="me-name"
            help={
              /* 帳號不能自己改，但它是登入時要打的那一串，一定要看得到 */
              myAccount ? (
                <>
                  登入帳號為 <strong>{myAccount}</strong>，如需變更請聯絡系統管理員。
                </>
              ) : null
            }
          >
            <TextField placeholder="請輸入姓名" id="me-name" fullWidth value={profileName} onChange={(e) => setProfileName(e.target.value)} />
          </Field>

          {/* 沒填 Email 的人只會收到站內通知，這件事要講出來，不然他不會知道自己漏收了什麼 */}
          <Field label="Email" htmlFor="me-mail" help="系統通知信將寄送至此信箱；未填寫時僅接收站內通知。">
            <TextField placeholder="例如 name@company.com" id="me-mail" fullWidth value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} />
          </Field>

          <Box>
            <Button variant="contained" type="button" onClick={saveProfile} aria-disabled={savingProfile} loading={savingProfile} loadingPosition="start">
              {savingProfile ? "儲存中…" : "儲存"}
            </Button>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard title="變更密碼" sx={CARD}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          變更後此裝置無須重新登入；其他已登入的裝置須待登入時效到期才會登出。
        </Typography>
        {/* 與 /Me/Password 共用同一份表單（components/auth/PasswordChangeForm） */}
        <PasswordChangeForm idPrefix="pref-pw" />
      </SectionCard>
    </Box>
  );

  /* ---------- 通知：我要收哪些通知（設計稿 L2028-2041） ---------- */
  const notifyPanel = (
    <SectionCard title="通知設定" sx={CARD}>
      <Typography sx={{ mt: 1 }}>以下列出各項通知的發送時機，請開啟需要接收的項目。</Typography>
      {/*
        這條橫幅只在「確定」沒在寄的時候出現。原本是無條件畫的，
        連 SMTP 開著、信天天在寄的時候也說「系統沒有寄送郵件」。
      */}
      {/*
        勾選照表單畫（管理者可能剛改、還沒存），但「系統沒在寄」是在講現在的事實，
        只看已存的值（mailStatus），跟系統管理分頁那一條同一個來源，兩邊才不會互相矛盾。
        表單與已存的值不一樣時，照實講「還沒存」，不然畫面上的勾選跟橫幅講的是兩回事。
      */}
      {mailPending ? (
        <Banner kind="info" sx={{ mt: 2 }}>
          <strong>郵件設定尚未儲存　</strong>
          {mailStatus === "on"
            ? "系統目前仍在寄送郵件。「系統管理」中的郵件寄送已關閉但尚未儲存，此處先依變更後的設定僅列出站內通知；於該分頁按下「儲存」後才會停止寄送。"
            : "系統目前尚未寄送郵件。「系統管理」中的郵件寄送已開啟但尚未儲存，此處先依變更後的設定列出 Email 選項；於該分頁按下「儲存」後才會開始寄送。"}
        </Banner>
      ) : mailState === "off" && mailStatus === "off" ? (
        <Banner kind="info" sx={{ mt: 2 }}>
          目前系統未啟用郵件寄送，此處僅列出站內通知；郵件選項將於系統啟用後顯示。
        </Banner>
      ) : mailState === "off" && mailStatus === "unknown" ? (
        /* 已存的狀態讀不到，不能說「系統沒在寄」；只講看得到的事：系統管理那邊的郵件開關是關的 */
        <Banner kind="info" sx={{ mt: 2 }}>
          「系統管理」中的郵件寄送設定目前為關閉，此處僅列出站內通知。
        </Banner>
      ) : null}
      {/*
        讀不到的時候不能假裝沒有郵件（那正是這次要修的 bug），也不能假裝知道有 ——
        照實講讀不到，並把寄信的勾選照樣列出來，讓想關的人關得掉（硬規則 4：後果寫成橫幅）。
      */}
      {mailState === "unknown" ? (
        <Banner kind="warn" icon={<MdWarningAmber />} sx={{ mt: 2 }}>
          <strong>無法讀取郵件設定　</strong>
          目前無法確認系統是否寄送郵件，因此仍列出「Email」欄：
          設定會照常儲存，系統寄送郵件時將依此設定寄送。可能為連線或伺服器問題，請重新整理後再試。
        </Banner>
      ) : null}

      {prefList.length === 0 ? (
        /* 還沒回來就什麼都不畫 —— 先畫四個空的勾再跳成別的值，看起來像自己被改掉了 */
        <SkeletonRows rows={4} sx={{ mt: 2 }} />
      ) : (
        /*
          一項一列的設定表：左邊是通知名稱與說明，右邊每個管道一欄開關，欄名寫在表頭。
          原本是五欄的勾選卡片，說明被擠成窄欄、每格高度參差，Email 那顆又縮排在底下，
          看不出它跟哪一項是一組。表格直接放在卡片裡，不另加外框（卡片本身就是框）。

          Email 那一欄只有在 SMTP 開著（或讀不到、不能確定沒開）的時候才畫
          （設計稿 L2033：「郵件開關等系統開通後才會出現」）；狀態還在讀的時候先不畫，
          免得畫出來又收回去。
        */
        <TableContainer sx={{ mt: 2 }}>
          <Table aria-label="通知設定">
            <TableHead>
              <TableRow>
                <TableCell>通知項目</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>
                  站內通知
                </TableCell>
                {showEmailRows ? (
                  <TableCell align="center" sx={{ width: 120 }}>
                    Email
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {prefList.map((r) => {
                /* 顯示文字先查正式用語對照（PREF_TEXT），對不到就用後端原文；送出仍用 r.type */
                const fixed = PREF_TEXT[r.label];
                const label = fixed ? fixed.label : r.label || r.type;
                const desc = fixed ? fixed.desc : r.desc && r.desc !== r.label ? r.desc : "";
                return (
                  <TableRow key={r.type} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell component="th" scope="row">
                      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                      {desc ? (
                        <Typography variant="body2" color="text.secondary">
                          {desc}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={!!r.in_app}
                        onChange={() => togglePref(r, "in_app")}
                        slotProps={{ input: { "aria-label": `${label}：站內通知` } }}
                      />
                    </TableCell>
                    {showEmailRows ? (
                      <TableCell align="center">
                        <Switch
                          checked={!!r.email}
                          onChange={() => togglePref(r, "email")}
                          slotProps={{ input: { "aria-label": `${label}：Email 通知` } }}
                        />
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );

  /* ---------- 顯示：顯示方式（設計稿 L2043-2056）與示範資料 ---------- */
  const displayPanel = (
    <>
      <SectionCard title="顯示方式" sx={CARD}>
        <Box sx={{ ...FORMGRID, mt: 2 }}>
          <Field label="字級" help="設定將保留至下次登入。「中」為標準大小；「大」會一併放大日期與代碼。">
            {/* 值 "" 是「中」；exclusive 群組再按一次已選的會回 null，所以擋掉 null */}
            <ToggleButtonGroup
              exclusive
              color="primary"
              aria-label="字級"
              value={textSize}
              onChange={(_e, v) => {
                if (v !== null) applySize(v);
              }}
              sx={TOGGLE_SX}
            >
              {TEXT_SIZES.map((o) => (
                <ToggleButton key={o.label} value={o.value} aria-label={`字級：${o.label}`} sx={{ fontSize: o.preview }}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Field>

          <Field label="深色模式（降低夜間使用時的刺眼感）" help="與字級相同，設定將保留至下次登入。">
            <ToggleButtonGroup
              exclusive
              color="primary"
              aria-label="深色模式（降低夜間使用時的刺眼感）"
              value={theme === "dark" ? "dark" : "light"}
              onChange={(_e, v) => {
                if (v) applyTheme(v === "dark");
              }}
              sx={TOGGLE_SX}
            >
              <ToggleButton value="light">淺色</ToggleButton>
              <ToggleButton value="dark">深色</ToggleButton>
            </ToggleButtonGroup>
          </Field>
        </Box>
      </SectionCard>

      {/*
        設計稿沒有這一塊，但它是既有功能（utils/request.js 的 X-Hide-Demo 標頭），
        刪掉等於把示範專案永遠釘死在每一頁上（硬規則 8）。放在「顯示」而不是「系統管理」的理由見 TABS。
      */}
      <SectionCard title="示範資料" sx={CARD}>
        <Box sx={{ mt: 1 }}>
          <CheckRow
            checked={hideDemo}
            onChange={toggleDemo}
            text="隱藏系統內建的示範專案"
            meta="示範專案代碼為 DEMO，隱藏僅過濾顯示、不會刪除，可隨時重新顯示。"
          />
        </Box>
        {/*
          「會整頁重載」是會弄丟輸入的後果，不是中性說明，所以寫成橫幅而不是
          勾選底下的灰字（硬規則 4）。姓名／Email、代碼前綴、SMTP 三組表單雖然在別的分頁，
          切分頁不會清掉它們，但重載會 —— 都要各自按下儲存鈕才送出，沒有任何草稿備份。
          橫幅放在 hideDemo 判斷之外：藏起來與顯示出來兩個方向都會重載。
        */}
        <Banner kind="warn" icon={<MdWarningAmber />} sx={{ mt: 2 }}>
          <strong>切換此選項將重新載入整頁　</strong>
          其他分頁中尚未儲存的姓名、Email、代碼前綴與郵件設定將會遺失，請先儲存再切換。
        </Banner>
        {hideDemo ? (
          <Banner kind="info" sx={{ mt: 2 }}>
            目前已隱藏示範資料。若尚未建立任何專案，多數頁面將不顯示資料，此為正常現象。
          </Banner>
        ) : null}
      </SectionCard>
    </>
  );

  /* ---------- 提醒：里程碑（設計稿 L2058-2065）與工項的兩個門檻 ---------- */
  /*
    兩張卡並排（FORMGRID 的兩欄，窄螢幕與大字模式回單欄）：卡片撐滿之後，
    一張卡裡只有一個下拉，右邊會空一大片；兩個門檻本來就是同一支端點、同一封信，並排剛好看得出是一組。
  */
  const remindPanel = (
    <Box sx={{ ...FORMGRID, ...CARD }}>
      <SectionCard title="里程碑的提醒門檻">
        <Typography sx={{ mt: 1 }}>
          工項排定後，距離里程碑的剩餘天數少於此設定值時，里程碑將以黃色提醒；超過里程碑日期後轉為紅色。
        </Typography>
        {buffer === null ? (
          /* 讀回來之前不畫下拉（同 SMTP 那一區的「載入中…」）。讀不到的話要講出來 ——
             留一個看起來正常、其實沒接上的欄位，比什麼都不顯示更糟 */
          bufferFailed ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              目前無法讀取此設定，可能為連線或伺服器問題，請重新整理後再試。
            </Typography>
          ) : (
            <InlineLoading sx={{ mt: 2 }} />
          )
        ) : (
          <>
            {/* 改不了的人看到的是一行字而不是一個不能選的下拉：
                停用狀態的欄位看起來像壞掉，而這裡只是「你不能改」（同專案編碼規則那一區） */}
            <Field
              label="提前提醒天數"
              htmlFor={canEditSmtp ? "buf" : ""}
              help={
                canEditSmtp
                  ? "此為全公司共用的設定值，並非各里程碑個別設定。儲存後，時程頁的里程碑將依新天數轉為黃燈，每日提醒信亦依此天數篩選里程碑。"
                  : "此為全公司共用的設定值。如需變更，須具備「系統設定－編輯」權限，請聯絡系統管理員。"
              }
              sx={{ mt: 2 }}
            >
              {canEditSmtp ? (
                <TextField
                  select
                  id="buf"
                  fullWidth
                  sx={NARROW}
                  value={buffer}
                  onChange={(e) => setBuffer(e.target.value)}
                  slotProps={{ select: { native: true } }}
                >
                  {bufferOptions.map((d) => (
                    <option key={d} value={String(d)}>
                      {d === DEFAULT_BUFFER_DAYS ? `${d} 天（預設）` : `${d} 天`}
                    </option>
                  ))}
                </TextField>
              ) : (
                <Typography sx={{ fontWeight: 600 }}>{buffer} 天</Typography>
              )}
            </Field>
            {/* 這個分頁有兩顆各管各的「存起來」，哪一顆都不比另一顆重要，都是 outlined（硬規則 1） */}
            {canEditSmtp ? (
              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
                <Button variant="outlined" type="button" onClick={saveBuffer} aria-disabled={savingBuffer} loading={savingBuffer} loadingPosition="start">
                  {savingBuffer ? "儲存中…" : "儲存"}
                </Button>
              </Stack>
            ) : null}
          </>
        )}
      </SectionCard>

      {/*
        設計稿沒有這一區（畫設計稿的時候工項根本還沒有到期提醒），但它與上面那個門檻
        是同一支端點的兩個值，所以緊接著放、而且整區的作法一律照上面那一區抄：
        同一種「載入中／讀不到」的講法、沒有 setting.edit 就給一行字而不是一個
        停用的下拉。兩個門檻長得不一樣的話，使用者會以為它們是兩種不同的東西。

        獨立成一張卡而不是塞進上面那一張：兩個值各自存、各有一顆「存起來」，
        併成一張卡的話會出現兩顆同名的鈕，誰管誰就看不出來了。
      */}
      <SectionCard title="工項的到期提醒">
        <Typography sx={{ mt: 1 }}>
          您負責的工項於此天數內到期或已逾期時，將彙整至每日提醒信中；
          已逾期與即將到期的工項會分開列出。
          未指派負責人、已完成或已關閉的工項不會發送提醒；所屬專案為「暫停」「已完成」「已取消」時亦不提醒。
        </Typography>
        {/*
          「一天一則、只在上班日、而且要先開通寄信」都是使用者會誤解的後果，
          所以寫成橫幅而不是底下的灰字（硬規則 4）。
          不能讓人以為存好之後就會即時跳出來提醒 —— 它是每天一次的彙總，
          跟里程碑提醒共用同一封信；SMTP 還沒開通時它根本不是一封信，只是右上角的一則通知。
        */}
        <Banner kind="info" sx={{ mt: 2 }}>
          <strong>每日僅發送一則　</strong>
          此提醒於上班日上午九點後彙整發送一次，週末與國定假日不發送，並非即時通知；里程碑的提醒亦包含在同一封信中。
          系統尚未啟用郵件寄送前，僅會顯示於右上角的通知中，不會寄送郵件。
        </Banner>
        {taskDue === null ? (
          /* 三種狀態的處理與里程碑那一區同一套：讀回來之前不畫下拉，讀不到就講出來 */
          taskDueFailed ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              目前無法讀取此設定，可能為連線或伺服器問題，請重新整理後再試。
            </Typography>
          ) : (
            <InlineLoading sx={{ mt: 2 }} />
          )
        ) : (
          <>
            {/* 改不了的人看到的是一行字而不是一個不能選的下拉（同里程碑那一區） */}
            <Field
              label="到期提醒天數"
              htmlFor={canEditSmtp ? "task-due" : ""}
              help={
                canEditSmtp
                  ? "此為全公司共用的設定值，並非每位使用者個別設定。提醒信僅寄送給該工項的負責人。如不需接收郵件，可於「通知」分頁關閉「即將到期」「已逾期」的 Email 開關；右上角的站內通知目前無法關閉。"
                  : "此為全公司共用的設定值。如需變更，須具備「系統設定－編輯」權限，請聯絡系統管理員。如不需接收郵件，可於「通知」分頁關閉「即將到期」「已逾期」的 Email 開關；右上角的站內通知目前無法關閉。"
              }
              sx={{ mt: 2 }}
            >
              {canEditSmtp ? (
                <TextField
                  select
                  id="task-due"
                  fullWidth
                  sx={NARROW}
                  value={taskDue}
                  onChange={(e) => setTaskDue(e.target.value)}
                  slotProps={{ select: { native: true } }}
                >
                  {taskDueOptions.map((d) => (
                    <option key={d} value={String(d)}>
                      {d === DEFAULT_TASK_DUE_DAYS ? `${d} 天（預設）` : `${d} 天`}
                    </option>
                  ))}
                </TextField>
              ) : (
                <Typography sx={{ fontWeight: 600 }}>{taskDue} 天</Typography>
              )}
            </Field>
            {/* 同上，outlined（硬規則 1） */}
            {canEditSmtp ? (
              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
                <Button variant="outlined" type="button" onClick={saveTaskDue} aria-disabled={savingTaskDue} loading={savingTaskDue} loadingPosition="start">
                  {savingTaskDue ? "儲存中…" : "儲存"}
                </Button>
              </Stack>
            ) : null}
          </>
        )}
      </SectionCard>
    </Box>
  );

  /* ---------- 專案編碼規則（設計稿 L2077-2086） ---------- */
  /*
    改不了、又讀不到值的人整塊不畫（硬規則 2）：沒有專案模組權限時
    /Project/CodeRule 會失敗，畫出來只是一個空的、而且是灰色不能打字的
    輸入框 —— 那看起來就是壞掉的欄位。
    有管理分頁的人放在「系統管理」；沒有的人放在「其他功能」看唯讀值 ——
    改版前一般使用者看得到目前的前綴，不能因為換成分頁就不見。
  */
  const codeRuleCard =
    canEditSmtp || codeRule.prefix ? (
      <SectionCard title="專案編碼規則" sx={CARD}>
        <Field
          label="代碼前綴"
          htmlFor={canEditSmtp ? "prefix" : ""}
          help={
            <>
              {codeRule.sample
                ? `建立專案時將自動產生 ${codeRule.sample} 格式的代碼（前綴－年月－流水號），流水號每月自 001 重新起算。`
                : "建立專案時將自動加上年月與流水號。"}
              {canEditSmtp
                ? " 前綴僅接受英數字，將自動轉為大寫，上限 8 碼。變更後僅套用於新建立的專案，既有專案代碼不變。"
                : " 如需變更前綴，須具備「系統設定－編輯」權限，請聯絡系統管理員。"}
            </>
          }
          sx={{ mt: 2 }}
        >
          {/* 改不了的人看到的是一行字而不是一個不能打字的輸入框：
              停用狀態的欄位看起來像壞掉，而這裡只是「你不能改」 */}
          {canEditSmtp ? (
            <TextField
              id="prefix"
              fullWidth
              sx={NARROW}
              value={prefixInput}
              onChange={(e) => setPrefixInput(e.target.value)}
              placeholder="PM"
            />
          ) : (
            <Typography sx={{ fontWeight: 600 }}>{codeRule.prefix}</Typography>
          )}
        </Field>
        {/* 系統管理分頁有編碼規則與 SMTP 兩顆各管各的存檔鈕，都是 outlined（硬規則 1） */}
        {canEditSmtp ? (
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
            <Button variant="outlined" type="button" onClick={saveCodeRule} aria-disabled={savingCode} loading={savingCode} loadingPosition="start">
              {savingCode ? "儲存中…" : "儲存"}
            </Button>
          </Stack>
        ) : null}
      </SectionCard>
    ) : null;

  /* ---------- 其他功能 ---------- */
  const otherPanel = (
    <>
      {plainPages.length ? (
        <Box component="section" sx={CARD}>
          <Typography>
            側邊欄僅保留常用入口。以下畫面仍可正常使用，因使用頻率較低而收納於此。
          </Typography>
          {pageRows(plainPages)}
        </Box>
      ) : null}
      {showAdminTab ? null : codeRuleCard}
    </>
  );

  /* ---------- 系統管理 ---------- */
  const adminPanel = (
    <>
      {/* 帳號與角色管理入口（設計稿 L2067-2075），加上只有管理者會開的那幾個畫面（見 OTHER_PAGES 的 admin） */}
      {canManageAdmin || adminPages.length ? (
        <Box component="section" sx={CARD}>
          <Typography variant="h3" component="h2">
            管理畫面
          </Typography>
          <Card sx={{ mt: 2 }}>
            <List disablePadding>
              {canManageAdmin ? (
                <RowButton
                  title="帳號與角色管理"
                  meta={<span>建立帳號、指派角色、停用帳號</span>}
                  side={<StatusChip kind="type">管理員</StatusChip>}
                  onClick={() => navigate("/Setting/Admin")}
                />
              ) : null}
              {adminPages.map((p) => (
                <RowButton key={p.path} title={p.label} meta={<span>{p.desc}</span>} onClick={() => navigate(p.path)} />
              ))}
            </List>
          </Card>
        </Box>
      ) : null}

      {/* 專案編碼規則：有管理權限的人放在這裡（可改）；其他人放在「其他功能」（唯讀），見 codeRuleCard */}
      {codeRuleCard}

      {/* ---------- 郵件寄送設定（SMTP） ---------- */}
      {/*
        設計稿沒有這一塊（它假設系統已經不寄信了），但 SMTP 設定與測試信都是
        既有功能，刪掉就再也開不起來（硬規則 8）。
      */}
      {canEditSmtp ? (
        <SectionCard title="郵件寄送設定（SMTP）" sx={CARD}>
          {/*
            這條以前無條件畫，SMTP 開著的時候也說「目前系統沒有寄送郵件」。
            現在只在 Status 確定關著時才出現；看的是已存的值（Status），不是表單上還沒存的勾 ——
            它講的是「現在」會不會寄，而會不會寄只看存進去的設定。讀不到就不講，不猜。
          */}
          {mailStatus === "off" ? (
            <Banner kind="info" sx={{ mt: 2 }}>
              <strong>目前系統未寄送郵件　</strong>
              以下為郵件伺服器設定，完成填寫並啟用後系統才會開始寄送郵件。
              在此之前，所有通知僅顯示於右上角的通知鈴鐺。
            </Banner>
          ) : null}

          {form ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <CheckRow
                checked={!!form.enabled}
                onChange={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
                text="啟用郵件通知"
                meta="關閉時系統不會寄送任何郵件，站內通知不受影響。"
              />

              {/* 八個欄位都短，撐滿的卡片裡兩欄並排；系統網址是整條網址，放不進半欄就跨整行 */}
              <Box sx={FORMGRID}>
                <Field label="SMTP 主機" htmlFor="smtp-host">
                  <TextField
                    id="smtp-host"
                    fullWidth
                    value={form.host || ""}
                    onChange={set("host")}
                    placeholder="smtp.office365.com"
                  />
                </Field>

                <Field label="連接埠" htmlFor="smtp-port">
                  <TextField id="smtp-port" fullWidth value={form.port || ""} onChange={set("port")} placeholder="587" />
                </Field>

                <Field label="加密方式" htmlFor="smtp-secure">
                  <TextField
                    select
                    id="smtp-secure"
                    fullWidth
                    value={form.secure || "starttls"}
                    onChange={set("secure")}
                    slotProps={{ select: { native: true } }}
                  >
                    {SECURE_OPTIONS.map((o) => (
                      <option key={o.v} value={o.v}>
                        {o.label}
                      </option>
                    ))}
                  </TextField>
                </Field>

                <Field label="帳號" htmlFor="smtp-user" help="內部轉遞主機若不需驗證，帳號可留空。">
                  <TextField placeholder="不需驗證時請留空" id="smtp-user" fullWidth value={form.user || ""} onChange={set("user")} />
                </Field>

                <Field
                  label="密碼"
                  htmlFor="smtp-pwd"
                  help={form.has_password ? "已設定，留空表示不變更。" : "尚未設定。"}
                >
                  <PasswordField
                    id="smtp-pwd"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={form.has_password ? "••••••••" : ""}
                    autoComplete="new-password"
                  />
                </Field>

                <Field label="寄件者信箱" htmlFor="smtp-from">
                  <TextField
                    id="smtp-from"
                    fullWidth
                    value={form.from_address || ""}
                    onChange={set("from_address")}
                    placeholder="no-reply@lifeplus.tw"
                  />
                </Field>

                <Field label="寄件者顯示名稱" htmlFor="smtp-fromname">
                  <TextField placeholder="例如 EL PM 通知" id="smtp-fromname" fullWidth value={form.from_name || ""} onChange={set("from_name")} />
                </Field>

                <Field
                  label="系統網址"
                  htmlFor="smtp-base"
                  help="信件中「在 EL PM 中開啟」按鈕的網址前綴；留空則不附連結。"
                  sx={{ gridColumn: "1 / -1" }}
                >
                  <TextField
                    id="smtp-base"
                    fullWidth
                    value={form.base_url || ""}
                    onChange={set("base_url")}
                    placeholder="https://project.lifeplus.net.tw"
                  />
                </Field>
              </Box>

              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <Button variant="outlined" type="button" onClick={saveSmtp} aria-disabled={saving} loading={saving} loadingPosition="start">
                  {saving ? "儲存中…" : "儲存"}
                </Button>
              </Stack>

              {/* 只有一格，放進 FORMGRID 會被 auto-fit 撐成整列，所以限制輸入框本身（同前綴） */}
              <Field
                label="寄送測試信"
                htmlFor="smtp-test"
                help={
                  <>
                    留空則寄送至您的信箱。測試信一律使用<strong>已儲存</strong>的設定寄出，變更後請先按上方的「儲存」。
                  </>
                }
              >
                <TextField
                  id="smtp-test"
                  fullWidth
                  sx={NARROW}
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="收件人 Email"
                />
              </Field>

              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <Button variant="outlined" type="button" onClick={sendTest} aria-disabled={testing} loading={testing} loadingPosition="start">
                  {testing ? "寄送中…" : "寄送測試信"}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <InlineLoading sx={{ mt: 2 }} />
          )}
        </SectionCard>
      ) : null}
    </>
  );

  const panels = {
    account: accountPanel,
    notify: notifyPanel,
    display: displayPanel,
    remind: remindPanel,
    other: otherPanel,
    admin: adminPanel,
  };

  return (
    <>
      <Typography variant="h1" component="h1">
        設定
      </Typography>

      {/*
        分頁列用 MUI Tabs（自帶 tablist / tab / aria-selected）；每個 tab 補上 id 與 aria-controls，
        下面的 tabpanel 才能用 aria-labelledby 指回去。最多六顆，手機一排放不下就左右捲動。
        只畫目前這一頁的內容：表單的值都存在這個元件的 state 裡，切走再切回來不會不見。
      */}
      <Tabs
        value={activeTab}
        onChange={(_e, key) => selectTab(key)}
        variant="scrollable"
        allowScrollButtonsMobile
        aria-label="設定分類"
        sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} id={`pref-tab-${t.key}`} aria-controls="pref-panel" />
        ))}
      </Tabs>

      <div id="pref-panel" role="tabpanel" aria-labelledby={`pref-tab-${activeTab}`}>
        {panels[activeTab]}
      </div>
    </>
  );
}

/*
 * SMTP、編碼規則與兩個提醒門檻（里程碑、工項）都是全公司共用的設定，只有具備「系統設定－編輯」的角色能改
 * （後端 PUT /Setting/Goal 也是 [PermissionOnly("setting.edit")]，與 PutSmtp 一致）。
 * canEditSmtp 這個名字比它實際的範圍窄，但它讀的就是 setting.edit，不另外加一個同義的 prop。
 * 帳號與角色管理的入口則看 admin —— AuthController 的 [PermissionOnly("admin")]。
 *
 * 兩個判斷讀的都是 access_token 的 permission claim，不是 Redux 裡的
 * Role.PermissionsUser（後者刻意只留 action='view' 的模組碼給側欄用）。
 * 詳見 utils/permission.js。這裡只影響畫面顯示，真正的把關一律在後端。
 */
const mapStateToProps = (state) => ({
  smtp: _.get(state, "Setting.Smtp", null),
  prefs: _.get(state, "Setting.NotificationPref", []),
  canEditSmtp: hasPermission("setting.edit"),
  canManageAdmin: hasPermission("admin"),
});

const mapDispatchToProps = (dispatch) => ({
  /* userInfo 是從 token 解出來的（見 saveProfile 的說明），改完個人資料要自己蓋回去 */
  SAVE_UserInfo: (payload) => dispatch({ type: "SAVE_UserInfo", payload }),
  GET_Smtp: (payload, callback, loading) => dispatch({ type: "GET_Smtp", payload, callback, loading }),
  PUT_Smtp: (payload, callback, loading) => dispatch({ type: "PUT_Smtp", payload, callback, loading }),
  POST_SmtpTest: (payload, callback, loading) => dispatch({ type: "POST_SmtpTest", payload, callback, loading }),
  GET_NotificationPref: (payload, callback, loading) =>
    dispatch({ type: "GET_NotificationPref", payload, callback, loading }),
  PUT_NotificationPref: (payload, callback, loading) =>
    dispatch({ type: "PUT_NotificationPref", payload, callback, loading }),
});

Preferences.propTypes = {
  smtp: PropTypes.object,
  prefs: PropTypes.array,
  canEditSmtp: PropTypes.bool,
  canManageAdmin: PropTypes.bool,
  SAVE_UserInfo: PropTypes.func,
  GET_Smtp: PropTypes.func,
  PUT_Smtp: PropTypes.func,
  POST_SmtpTest: PropTypes.func,
  GET_NotificationPref: PropTypes.func,
  PUT_NotificationPref: PropTypes.func,
};

Preferences.defaultProps = {
  smtp: null,
  prefs: [],
  canEditSmtp: false,
  canManageAdmin: false,
  SAVE_UserInfo: () => {},
  GET_Smtp: () => {},
  PUT_Smtp: () => {},
  POST_SmtpTest: () => {},
  GET_NotificationPref: () => {},
  PUT_NotificationPref: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(Preferences);
